import { QueryClient, dehydrate } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      networkMode: "offlineFirst",
      retry: 1,
    },
  },
});

export const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
});

// live-table-data is excluded from persistence — always fresh on page load.
// Column prefs are persisted and updated on mutation via persistColumnPrefs().
export const persistOptions = {
  persister,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: any) =>
      query.queryKey?.[0] !== "live-table-data",
  },
};

export function syncPersistedCache() {
  persister.persistClient({
    clientState: dehydrate(queryClient, persistOptions.dehydrateOptions),
    timestamp: Date.now(),
    buster: "",
  });
}


// ---------------------------------------------------------------------------
// Table cache invalidation — callable from plain JS as window.invalidateTableCache()
// ---------------------------------------------------------------------------

/**
 * Invalidate table cache and trigger refetch for mounted tables.
 *
 * @param apiUrl — e.g. "/admin/peer/peer/table_data/". If omitted, invalidates ALL tables.
 *
 * Usage from plain JS:
 *   window.invalidateTableCache("/admin/peer/peer/table_data/")
 *   window.invalidateTableCache()  // all tables
 */
export function invalidateTableCache(apiUrl?: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      if (query.queryKey?.[0] !== "live-table-data") return false;
      return !apiUrl || query.queryKey?.[1] === apiUrl;
    },
    refetchType: "active",
  });
}

(window as any).invalidateTableCache = invalidateTableCache;
